import React from 'react';
import { createPortal } from 'react-dom';
import { usePomodoro } from '../context/PomodoroContext';
import { useApp } from '../context/AppContext';
import { getColors, getModalStyle } from '../theme';

/**
 * Shown when a focus session ends: what you worked on, what's next,
 * and the choice to finish the task or take the break.
 */
const PomodoroCompletionModal = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const {
    showCompletionModal,
    activeTaskId,
    activeTaskTitle,
    completeActiveTask,
    startBreak,
    sessionsCompleted,
    settings,
  } = usePomodoro();

  const handleCompleteTask = () => {
    completeActiveTask();
  };

  if (!showCompletionModal) return null;

  const isLongBreak = sessionsCompleted % settings.sessionsBeforeLongBreak === 0;
  const rule = `1px solid ${colors.border}`;
  const rows = [
    activeTaskTitle && { label: 'Worked on', value: activeTaskTitle },
    {
      label: 'Up next',
      value: isLongBreak ? `${settings.longBreakDuration} min long break` : `${settings.shortBreakDuration} min short break`,
    },
  ].filter(Boolean);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/60 animate-fade-in">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pomodoro-done-title"
        className="w-full sm:max-w-sm animate-scale-up"
        style={getModalStyle(colors)}
      >
        <div className="px-5 pt-6 pb-5">
          <p className="font-mono text-xs mb-2" style={{ color: colors.textSecondary }}>
            Session {sessionsCompleted} today · {settings.workDuration} min
          </p>
          <h3 id="pomodoro-done-title" className="text-[28px] font-semibold leading-tight" style={{ color: colors.textPrimary, letterSpacing: '-0.035em' }}>
            Focus session done.
          </h3>
        </div>

        <dl className="px-5" style={{ borderTop: `1px solid ${colors.textPrimary}` }}>
          {rows.map(({ label, value }) => (
            <div key={label} className="grid grid-cols-[84px_minmax(0,1fr)] items-center min-h-[48px]" style={{ borderBottom: rule }}>
              <dt className="font-mono text-xs self-stretch flex items-center" style={{ color: colors.textSecondary, borderRight: rule }}>{label}</dt>
              <dd className="pl-3 py-3 text-[15px] truncate" style={{ color: colors.textPrimary }}>{value}</dd>
            </div>
          ))}
        </dl>

        <div className="p-5 grid gap-3">
          {activeTaskId && (
            <button
              onClick={handleCompleteTask}
              className="w-full min-h-[46px] text-sm font-medium transition-opacity hover:opacity-85"
              style={{ backgroundColor: colors.textPrimary, color: colors.onAccent }}
            >
              Mark task complete
            </button>
          )}
          <button
            onClick={startBreak}
            className="w-full min-h-[46px] text-sm font-medium transition-opacity hover:opacity-80"
            style={activeTaskId
              ? { color: colors.textPrimary, border: `1px solid ${colors.borderStrong}` }
              : { backgroundColor: colors.textPrimary, color: colors.onAccent }}
          >
            {activeTaskId ? 'Take the break instead' : 'Start break'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PomodoroCompletionModal;
